import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE, Kinetic } from "@/components/motion";
import { IMAGES } from "@/data/site";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative h-screen min-h-[640px] w-full overflow-hidden bg-ink">
      <motion.div className="absolute inset-0" style={{ y }}>
        <motion.img
          src={IMAGES.homeHero}
          alt="The Himalayas at first light"
          className="h-full w-full scale-[1.08] object-cover"
          initial={{ scale: 1.22 }}
          animate={{ scale: 1.08 }}
          transition={{ duration: 2.8, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/25" />

      <div className="relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-center px-6 lg:px-12">
        <motion.div
          className="flex items-center gap-4"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
        >
          <span className="gold-rule" />
          <span data-testid="hero-label" className="label text-white/85">Urban Trail</span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="mt-8 font-serif text-[13vw] font-light leading-[0.94] tracking-tight text-white sm:text-7xl lg:text-8xl xl:text-[7.5rem]"
        >
          <Kinetic lines={["Journeys Worth", "Remembering."]} delay={0.35} />
        </h1>

        <motion.p
          data-testid="hero-subcopy"
          className="mt-8 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.95, ease: EASE }}
        >
          Thoughtfully curated journeys across remarkable destinations, meaningful
          experiences and places that stay with you long after you return.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-col gap-4 sm:flex-row"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1, ease: EASE }}
        >
          <Link to="/contact" data-testid="hero-plan-journey" className="btn-outline-light !border-white !bg-white !text-ink hover:!bg-gold hover:!border-gold hover:!text-white">
            Plan Your Journey
          </Link>
          <Link to="/destinations" data-testid="hero-explore-destinations" className="btn-outline-light">
            Explore Destinations
          </Link>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] uppercase tracking-[0.4em] text-white/60">Scroll to Explore</span>
        <div className="h-10 w-px overflow-hidden bg-white/20">
          <motion.div
            className="h-1/2 w-px bg-gold"
            animate={{ y: ["-100%", "220%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
}
